import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  VStack,
  Text,
  Box,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  Heading,
  SimpleGrid,
  Badge,
  IconButton,
  SlideFade,
  Collapse,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import wc from "../../../../assets/img/chargePointsTab/white_config.png";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import useAxios from "axios-hooks";

const ChargerConfig = ({ stationId }) => {
  const { isOpen, onOpen, onClose: baseOnClose } = useDisclosure();
  const {
    isOpen: isConfigOpen,
    onOpen: onConfigOpen,
    onClose: onConfigClose,
  } = useDisclosure();
  const [config, setConfig] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [newValue, setNewValue] = useState("");
  const setToast = useSetRecoilState(toastState);

  // Handle main modal close with cleanup
  const onClose = () => {
    setConfig([]);
    setSearchQuery("");
    setSelectedConfig(null);
    setNewValue("");
    onConfigClose();
    baseOnClose();
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleConfigSelect = (item) => {
    setSelectedConfig(item);
    setNewValue(item.value);
    onConfigOpen();
  };

  const handleClickOutside = () => {
    setSelectedConfig(null);
    setNewValue("");
    onConfigClose();
  };

  const filteredConfigs = config.filter((item) =>
    item.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const readOnlyConfigs = filteredConfigs.filter((item) => item.readonly);
  const editableConfigs = filteredConfigs.filter((item) => !item.readonly);

  const [{ data, loading, error }, execute, cancelRequest] = useAxios(
    { method: "POST", url: "/api/ocpp16/getConfiguration" },
    { manual: true }
  );

  const [
    { data: updateData, loading: updateLoading, error: updateError },
    executeUpdate,
    cancelUpdateRequest,
  ] = useAxios(
    { method: "POST", url: "/api/ocpp16/changeConfiguration" },
    { manual: true }
  );

  const handleSave = async () => {
    try {
      const body = {
        StationId: stationId,
        KeyValuePair: {
          Key: selectedConfig.key,
          Value: newValue,
        },
      };
      const response = await executeUpdate({ data: body });
      setToast({
        isOpen: true,
        title: "Success",
        description: "Configuration changed successfully",
        status: "success",
      });
      await getConfig();
      handleClickOutside(); // Close the modal
    } catch (error) {
      setToast({
        isOpen: true,
        title: error?.response?.data?.metadata?.error_code
          ? error?.response?.data?.metadata?.error_code
          : "Error occured while updating Config",
        description: error?.response?.data?.metadata?.error_message
          ? error?.response?.data?.metadata?.error_message
          : null,
        status: "error",
      });
    }
  };

  async function getConfig() {
    try {
      const body = {
        StationId: stationId,
      };
      const response = await execute({ data: body });
      setConfig(response?.data?.data?.configurationKey);
    } catch (error) {
      logger.log(error);
      setToast({
        isOpen: true,
        title: error?.response?.data?.metadata?.error_code
          ? error?.response?.data?.metadata?.error_code
          : "Error occured while getting Config",
        description: error?.response?.data?.metadata?.error_message
          ? error?.response?.data?.metadata?.error_message
          : null,
        status: "error",
      });
      onClose();
    }
  }

  useEffect(() => {
    if (isOpen) {
      getConfig();
    }
    return () => {
      cancelRequest();
    };
  }, [isOpen]);

  return (
    <>
      <button className="acCharger_button configuration" onClick={onOpen}>
        <img
          src={wc}
          alt="configuration_icon"
          style={{
            display: "inline",
            marginRight: "0.44rem",
          }}
        />
        <p>Configuration</p>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        isCentered
        motionPreset="scale"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent maxH="90vh" maxW="90%" mx="auto">
          <ModalHeader
            display="flex"
            justifyContent="center"
            borderBottom="1px"
            borderColor="gray.200"
            py={4}
          >
            <Text fontSize="3xl" fontWeight="600">
              {stationId} : Configuration
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Heading size="md" mb={4}>
                  Search Configurations
                </Heading>
                <InputGroup size="md">
                  <Input
                    placeholder="Search configuration by name..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    bg="white"
                    borderWidth="1px"
                    _focus={{ borderColor: "blue.500", boxShadow: "sm" }}
                  />
                  <InputRightElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputRightElement>
                </InputGroup>
              </Box>

              <Box maxH="calc(90vh - 400px)" overflowY="auto">
                <Box mb={6}>
                  <Heading
                    size="md"
                    mb={4}
                    display="flex"
                    alignItems="center"
                    color="gray.700"
                  >
                    Read Only Configurations
                    {readOnlyConfigs.length > 0 && (
                      <Badge ml={2} colorScheme="gray">
                        {readOnlyConfigs.length}
                      </Badge>
                    )}
                  </Heading>
                  {readOnlyConfigs.length === 0 ? (
                    <Box
                      p={4}
                      textAlign="center"
                      borderWidth="1px"
                      borderRadius="lg"
                      bg="gray.50"
                    >
                      <Text color="gray.600">
                        No read-only configurations available
                      </Text>
                    </Box>
                  ) : (
                    <SimpleGrid
                      columns={{
                        base: 1, // 0-48em (0-768px)
                        md: 2, // 48em-80em (768px-1280px)
                        lg: 3, // 80em+ (1280px+)
                        xl: 4, // 96em+ (1536px+)
                      }}
                      spacing={{ base: 3, md: 4, lg: 5 }}
                      width="100%"
                      maxW="90%"
                      mx="auto"
                    >
                      {readOnlyConfigs.map((item) => (
                        <Box
                          key={item.key}
                          p={4}
                          borderWidth="1px"
                          borderRadius="lg"
                          cursor="pointer"
                          onClick={() => handleConfigSelect(item)}
                          _hover={{
                            bg: "gray.50",
                            borderColor: "gray.300",
                            transform: "translateY(-1px)",
                            transition: "all 0.2s",
                            boxShadow: "sm",
                          }}
                          bg={
                            selectedConfig?.key === item.key
                              ? "blue.50"
                              : "white"
                          }
                          borderColor={
                            selectedConfig?.key === item.key
                              ? "blue.200"
                              : "gray.200"
                          }
                          transition="all 0.2s"
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}
                          >
                            <Text
                              fontWeight="bold"
                              fontSize="sm"
                              noOfLines={1}
                              flex="1"
                            >
                              {item.key}
                            </Text>
                            <Badge colorScheme="red" fontSize="xs">
                              Read Only
                            </Badge>
                          </Box>
                          <Text fontSize="sm" color="gray.600" noOfLines={1}>
                            Value: {item.value}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  )}
                </Box>

                <Box>
                  <Heading
                    size="md"
                    mb={4}
                    display="flex"
                    alignItems="center"
                    color="gray.700"
                  >
                    Editable Configurations
                    {editableConfigs.length > 0 && (
                      <Badge ml={2} colorScheme="gray">
                        {editableConfigs.length}
                      </Badge>
                    )}
                  </Heading>
                  {editableConfigs.length === 0 ? (
                    <Box
                      p={4}
                      textAlign="center"
                      borderWidth="1px"
                      borderRadius="lg"
                      bg="gray.50"
                    >
                      <Text color="gray.600">
                        No editable configurations available
                      </Text>
                    </Box>
                  ) : (
                    <SimpleGrid
                      columns={{
                        base: 1, // 0-48em (0-768px)
                        md: 2, // 48em-80em (768px-1280px)
                        lg: 3, // 80em+ (1280px+)
                        xl: 4, // 96em+ (1536px+)
                      }}
                      spacing={{ base: 3, md: 4, lg: 5 }}
                      width="100%"
                      maxW="90%"
                      mx="auto"
                    >
                      {editableConfigs.map((item) => (
                        <Box
                          key={item.key}
                          p={4}
                          borderWidth="1px"
                          borderRadius="lg"
                          cursor="pointer"
                          onClick={() => handleConfigSelect(item)}
                          _hover={{
                            bg: "gray.50",
                            borderColor: "gray.300",
                            transform: "translateY(-1px)",
                            transition: "all 0.2s",
                            boxShadow: "sm",
                          }}
                          bg={
                            selectedConfig?.key === item.key
                              ? "blue.50"
                              : "white"
                          }
                          borderColor={
                            selectedConfig?.key === item.key
                              ? "blue.200"
                              : "gray.200"
                          }
                          transition="all 0.2s"
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}
                          >
                            <Text
                              fontWeight="bold"
                              fontSize="sm"
                              noOfLines={1}
                              flex="1"
                            >
                              {item.key}
                            </Text>
                            <Badge colorScheme="green" fontSize="xs">
                              Editable
                            </Badge>
                          </Box>
                          <Text fontSize="sm" color="gray.600" noOfLines={1}>
                            Value: {item.value}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  )}
                </Box>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isConfigOpen}
        onClose={handleClickOutside}
        isCentered
        motionPreset="scale"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent>
          <Box p={6} position="relative">
            <IconButton
              icon={<CloseIcon />}
              size="sm"
              variant="ghost"
              position="absolute"
              right={2}
              top={2}
              onClick={handleClickOutside}
              aria-label="Close"
            />
            {selectedConfig && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" fontSize="lg" mb={1}>
                    {selectedConfig.key}
                  </Text>
                  <Badge
                    colorScheme={selectedConfig.readonly ? "red" : "green"}
                  >
                    {selectedConfig.readonly ? "Read Only" : "Editable"}
                  </Badge>
                </Box>

                <FormControl>
                  <FormLabel>Current Value</FormLabel>
                  <Text fontWeight="medium">{selectedConfig.value}</Text>
                </FormControl>

                <FormControl>
                  <FormLabel>New Value</FormLabel>
                  <Input
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    isDisabled={selectedConfig.readonly}
                    bg="white"
                  />
                </FormControl>

                <Button
                  colorScheme="brand"
                  bg="brand.500"
                  onClick={handleSave}
                  isDisabled={selectedConfig.readonly}
                  isLoading={updateLoading}
                >
                  Save Changes
                </Button>
              </VStack>
            )}
          </Box>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ChargerConfig;
